/* ═══════════════════════════════════════════════════════════════════════════
   Chase N Chew — Wishlist page + Recently Viewed rail

   Both surfaces render from localStorage, which is the whole reason they need
   a script at all: Liquid cannot see the wishlist. The store is the HOST
   THEME's, not a private one —

     glozin__wishlist-items   an array of bare product-id STRINGS. The theme's
                              own header counter and wishlist page parse it, so
                              its shape is not ours to change.
     cnc:wishlist-meta        { "<id>": { handle, title, url, img, price,
                              compare, variant, nvariants } }. Sidecar, written
                              by cnc-app.js when a heart is tapped, because
                              Shopify's storefront API resolves products by
                              HANDLE and never by numeric id — there is no
                              request that turns an id back into a title.
     cnc:recently-viewed      most-recent-first, deduped, capped at 12.

   Everything that touches the cart or a variant goes through cnc-app.js's
   exports (window.CNC.addToCart / openPicker / rescan) rather than being
   rebuilt here. Two implementations of "add to this cart" is how a storefront
   ends up with two different bugs.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  "use strict";

  var WISH_KEY = "glozin__wishlist-items";
  var WISH_META_KEY = "cnc:wishlist-meta";
  var VIEWED_KEY = "cnc:recently-viewed";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var ICON = {
    bag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6Z"/><path d="M3 6h18M16 10a4 4 0 0 1-8 0"/></svg>',
    trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>',
    heart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-7.5-4.7-9.3-9A5.3 5.3 0 0 1 12 6.7 5.3 5.3 0 0 1 21.3 12c-1.8 4.3-9.3 9-9.3 9Z"/></svg>',
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
    sliders: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/></svg>',
    chevL: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>',
    chevR: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>',
    arrowR: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14m0 0-7-7m7 7-7 7"/></svg>'
  };

  /* ── Small shared helpers ─────────────────────────────────────────────── */

  function readJson(key, fallback) {
    try { return JSON.parse(localStorage.getItem(key)) || fallback; }
    catch (e) { return fallback; }
  }
  function writeJson(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* quota */ }
  }
  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }
  /* Indian grouping (12,34,567), same as cnc-app.js. Rupees in, not paise. */
  function money(rs) {
    var n = Math.round((rs || 0) * 100) / 100;
    var s = (Number.isInteger(n) ? String(n) : n.toFixed(2)).split(".");
    var x = s[0], last3 = x.slice(-3), rest = x.slice(0, -3);
    if (rest) last3 = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
    return "₹" + last3 + (s[1] ? "." + s[1] : "");
  }
  function handleFromUrl(url) {
    var m = /\/products\/([^/?#]+)/.exec(String(url || ""));
    return m ? m[1] : "";
  }
  function api(name) {
    return window.CNC && typeof window.CNC[name] === "function" ? window.CNC[name] : null;
  }

  /* ── The wishlist store ───────────────────────────────────────────────── */

  function readWish() {
    var list = readJson(WISH_KEY, []);
    return Array.isArray(list) ? list.map(String) : [];
  }

  /* A deliberate mirror of writeWish() in cnc-app.js, and the duplication is
     load-bearing rather than lazy: this page has to REMOVE items and put them
     back for Undo, and the write is not one call — it is the localStorage set,
     the aria-pressed repaint, the host theme's `.wishlist-count` nodes, a
     synthetic StorageEvent for anything listening cross-tab, and our own
     cnc:wishlist-updated. Miss one and the header badge lies until reload.
     If the key or the announcement ever changes, both copies change. */
  function writeWish(list) {
    var json = JSON.stringify(list);
    try { localStorage.setItem(WISH_KEY, json); } catch (e) { /* quota */ }

    document.querySelectorAll("[data-wish]").forEach(function (b) {
      b.setAttribute("aria-pressed", String(list.indexOf(String(b.dataset.wish)) > -1));
    });
    document.querySelectorAll(".wishlist-count").forEach(function (el) {
      el.textContent = list.length;
    });
    try {
      window.dispatchEvent(new StorageEvent("storage", {
        key: WISH_KEY, newValue: json, storageArea: localStorage
      }));
    } catch (e) { /* older browsers — the direct updates above still ran */ }
    document.dispatchEvent(new CustomEvent("cnc:wishlist-updated", { detail: { items: list } }));
  }

  /* Saved items, in saved order, as renderable objects. Ids with no meta are
     dropped rather than drawn as blanks — cnc-app.js backfills them from the
     catalogue on the next drawer open, at which point they appear here too. */
  function savedItems() {
    var meta = readJson(WISH_META_KEY, {});
    return readWish().filter(function (id) { return meta[id]; }).map(function (id) {
      var m = meta[id];
      return {
        id: String(id),
        title: m.title || "",
        url: m.url || (m.handle ? "/products/" + m.handle : "#"),
        img: m.img || "",
        price: m.price || 0,
        compare: m.compare || 0,
        variant: m.variant || 0,
        nvariants: m.nvariants || 1,
        handle: m.handle || handleFromUrl(m.url)
      };
    });
  }

  function viewedItems() {
    var list = readJson(VIEWED_KEY, []);
    if (!Array.isArray(list)) return [];
    return list.filter(function (p) { return p && p.id; }).map(function (p) {
      return {
        id: String(p.id),
        title: p.title || "",
        url: p.url || (p.handle ? "/products/" + p.handle : "#"),
        img: p.img || "",
        price: p.price || 0,
        compare: p.compare || 0,
        variant: p.variant || 0,
        nvariants: p.nvariants || 1,
        handle: p.handle || handleFromUrl(p.url)
      };
    });
  }

  /* ── The card ─────────────────────────────────────────────────────────────
     Deliberately the SAME markup the Liquid snippet emits — .cnc-card,
     .cnc-card-img, .cnc-card-info, .cnc-price-row — so one stylesheet dresses
     both and a saved card cannot drift away from a shop card by accident.

     The one thing it cannot reproduce is the Liquid form tag the shop card
     wraps its Add button in, which is what hands the add to the host theme's
     own <product-form>. A card built in the browser has no such tag, so its
     button posts through window.CNC.addToCart instead — the same two
     documented endpoints that handler uses, exported for exactly this. */
  function priceRow(it) {
    var off = it.compare && it.compare > it.price;
    // Borrowed, not recomputed. window.CNC.discPct is the one discount rule in
    // the theme (round up); see the note above it in cnc-app.js, which every
    // section loading this file loads first.
    var pctOf = api("discPct");
    var pct = pctOf ? pctOf(it.price, it.compare) : 0;
    return '<div class="cnc-price-row">'
      + '<span class="cnc-price">' + money(it.price / 100) + "</span>"
      + (off ? '<span class="cnc-mrp">' + money(it.compare / 100) + "</span>" : "")
      + (pct > 0 ? '<span class="cnc-disc">' + pct + "% OFF</span>" : "")
      + "</div>";
  }

  /* ── Image sizing ─────────────────────────────────────────────────────────
     The saved meta stores whatever width the heart happened to capture, and
     that width was chosen for a different surface: `image_url: width: 200`,
     sized for the cart drawer's 120px rail thumbnail. A card draws the same
     photo at up to 300 CSS px, and on a 2x screen that wants 600 — so the
     stored URL upscales, and the card looks soft.

     The fix is a rewrite at RENDER time, not a bigger capture. The meta lives
     on the customer's device; there is no migration we can run over it, so
     changing what gets saved would only help people who re-heart everything.
     Rewriting the width fixes every wishlist already out there, tonight.

     Two URL shapes, because Shopify has printed both over the years:

       .../foo.jpg?v=123&width=200        current
       .../foo_200x.jpg?v=123             older _WxH filename suffix

     Anything else — a data: URI, a bare CDN original, another host — comes
     back untouched, which is the right answer for all three: the original is
     already full size, and we have no business rewriting a stranger's URL. */
  function cdnImg(url, w) {
    var u = String(url || "");
    if (!u) return "";
    if (/[?&]width=\d+/.test(u)) return u.replace(/([?&]width=)\d+/, "$1" + w);
    var suffix = /_\d+x\d*(_crop_[a-z]+)?\.(jpg|jpeg|png|webp|gif)/i;
    if (suffix.test(u)) return u.replace(suffix, "_" + w + "x$1.$2");
    return u;
  }

  /* ── The corner badge ────────────────────────────────────────────────────
     Resolved by cnc-badge on the server and handed over as a handle -> badge
     map, because a badge here is mostly earned by collection membership and no
     storefront JSON endpoint tells the browser a product's collections. This
     file therefore decides nothing about what a badge says — it only places
     the one it was given, which is what keeps a saved card and a shop card
     showing the same thing.

     Absent map, absent handle, malformed JSON: no badge and no error. This is
     decoration on a page whose job is showing saved items. */
  var BADGES = (function () {
    var el = document.querySelector("[data-cnc-badgemap]");
    if (!el) return {};
    var map = {};
    try {
      /* [handle, key, LABEL] triples — an array rather than an object because
         a literal curly brace inside a Liquid output tag terminates the tag. */
      (JSON.parse(el.textContent || "[]") || []).forEach(function (row) {
        if (row && row.length === 3) map[row[0]] = [row[1], row[2]];
      });
    } catch (e) {
      console.warn("[cnc-wishlist] badge map unreadable", e);
    }
    return map;
  })();

  /* A SIBLING of the image link, never inside it — the same rule the Liquid
     card follows. The link carries aria-label="{title}", and an aria-label
     replaces its element's contents, so a ribbon nested in there is announced
     to nobody. */
  function ribbonHtml(it) {
    var b = BADGES[it.handle];
    if (!b || !b.length) return "";
    return '<span class="cnc-ribbon cnc-ribbon--' + esc(b[0]) + '">' + esc(b[1]) + "</span>";
  }

  /* Same srcset and sizes the Liquid card emits, so a saved card and a shop
     card ask the network for exactly the same file at the same breakpoint —
     which also means the second one is already in cache. */
  function imgBlock(it) {
    if (!it.img) {
      return '<a href="' + esc(it.url) + '" class="cnc-card-img" aria-label="'
        + esc(it.title) + '"></a>';
    }
    var big = cdnImg(it.img, 600);
    var set = big === it.img ? "" : ' srcset="' + esc(cdnImg(it.img, 300)) + " 300w, "
      + esc(big) + " 600w, " + esc(cdnImg(it.img, 900)) + ' 900w"'
      + ' sizes="(min-width: 1024px) 300px, 46vw"';
    return '<a href="' + esc(it.url) + '" class="cnc-card-img" aria-label="' + esc(it.title) + '">'
      + '<img src="' + esc(big) + '"' + set + ' alt="' + esc(it.title)
      + '" width="600" height="600" loading="lazy">'
      + "</a>";
  }

  /* Shared attribute bundle for anything that adds to the cart from a
     client-rendered card. `multi` decides between a straight add and the
     Pick & Pack sheet, and `mode` decides whether the item also leaves the
     wishlist on the way — the difference between Move and Add. */
  function addAttrs(it, mode) {
    var multi = (it.nvariants || 1) > 1 || !it.variant;
    return ' data-cnc-jsadd="' + esc(it.variant || "") + '"'
      + ' data-cnc-jsmulti="' + (multi ? "1" : "0") + '"'
      + ' data-cnc-jsid="' + esc(it.id) + '"'
      + ' data-cnc-jshandle="' + esc(it.handle) + '"'
      + ' data-cnc-jsurl="' + esc(it.url) + '"'
      + ' data-cnc-jsmode="' + mode + '"';
  }

  function savedCardHtml(it) {
    var multi = (it.nvariants || 1) > 1;
    return '<div class="cnc-card cnc-wl-card" data-wl-card="' + esc(it.id) + '">'
      + imgBlock(it)
      + ribbonHtml(it)
      + '<div class="cnc-card-info">'
      + '<div class="cnc-card-brand">Chase N Chew</div>'
      + '<a href="' + esc(it.url) + '" class="cnc-card-title">' + esc(it.title) + "</a>"
      + priceRow(it)
      + (multi
          ? '<span class="cnc-wl-hint" data-wl-hint>' + it.nvariants + " options</span>"
          : '<span class="cnc-wl-hint" data-wl-hint hidden></span>')
      + '<div class="cnc-wl-spacer"></div>'
      + '<div class="cnc-wl-actions">'
      + '<button type="button" class="cnc-wl-move"' + addAttrs(it, "move")
      + ' aria-label="Move ' + esc(it.title) + ' to bag">'
      + ICON.bag + "<span>Move to bag</span></button>"
      + '<button type="button" class="cnc-wl-del" data-wl-remove="' + esc(it.id) + '"'
      + ' title="Remove from wishlist"'
      + ' aria-label="Remove ' + esc(it.title) + ' from wishlist">' + ICON.trash + "</button>"
      + "</div></div></div>";
  }

  /* The rail card is the shop card, heart and all. The heart carries the full
     data-wish-* bundle because cnc-app.js copies those attributes into the
     meta sidecar on toggle — a heart without them saves an id that nothing
     can later render. */
  function viewedCardHtml(it) {
    return '<div class="cnc-card" data-rv-card="' + esc(it.id) + '">'
      + imgBlock(it)
      + ribbonHtml(it)
      + '<button type="button" class="cnc-wish" data-wish="' + esc(it.id) + '"'
      + ' data-wish-handle="' + esc(it.handle) + '"'
      + ' data-wish-title="' + esc(it.title) + '"'
      + ' data-wish-url="' + esc(it.url) + '"'
      + ' data-wish-img="' + esc(it.img) + '"'
      + ' data-wish-price="' + esc(it.price) + '"'
      + ' data-wish-compare="' + esc(it.compare || "") + '"'
      + ' data-wish-variant="' + esc(it.variant || "") + '"'
      + ' data-wish-nvariants="' + esc(it.nvariants || 1) + '"'
      + ' aria-pressed="false" aria-label="Add ' + esc(it.title) + ' to wishlist">'
      + ICON.heart + "</button>"
      + '<button type="button" class="cnc-card-add"' + addAttrs(it, "add")
      + ' aria-label="Add ' + esc(it.title) + ' to cart">' + ICON.plus + "</button>"
      + '<div class="cnc-card-bagform">'
      + '<button type="button" class="cnc-card-bag cnc-jsbag"' + addAttrs(it, "add")
      + ' aria-label="Add ' + esc(it.title) + ' to bag">'
      + ICON.bag + "<span>Add to bag</span></button></div>"
      + '<div class="cnc-card-info">'
      + '<div class="cnc-card-brand">Chase N Chew</div>'
      + '<a href="' + esc(it.url) + '" class="cnc-card-title">' + esc(it.title) + "</a>"
      + priceRow(it)
      + "</div></div>";
  }

  /* ── Toast ────────────────────────────────────────────────────────────────
     One node, reused, with one optional action. It is what lets Remove be a
     single tap with no confirmation dialog: the cost of a mistake becomes one
     more tap rather than a lost list, which is a far better trade than making
     every correct removal answer a question. */
  var toastTimer = null;

  function toast(msg, actionLabel, onAction) {
    var el = document.querySelector(".cnc-toast");
    if (!el) {
      el = document.createElement("div");
      el.className = "cnc cnc-toast";
      el.setAttribute("role", "status");
      el.innerHTML = '<span class="cnc-toast-msg"></span>'
        + '<button type="button" class="cnc-toast-act" hidden></button>';
      document.body.appendChild(el);
    }
    el.querySelector(".cnc-toast-msg").textContent = msg;

    /* The action button is replaced rather than re-bound. Rebinding leaks a
       listener per toast, and the previous toast's Undo would still be live
       under the new toast's label — undoing something the customer is no
       longer looking at. */
    var oldBtn = el.querySelector(".cnc-toast-act");
    var btn = oldBtn.cloneNode(false);
    oldBtn.parentNode.replaceChild(btn, oldBtn);
    btn.hidden = !actionLabel;
    if (actionLabel) {
      btn.textContent = actionLabel;
      btn.addEventListener("click", function () {
        el.classList.remove("is-on");
        if (onAction) onAction();
      });
    }

    el.classList.add("is-on");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove("is-on"); }, 5200);
  }

  /* ── Wishlist page ────────────────────────────────────────────────────── */

  var lastRemoved = null;   /* { id, meta, at } — the one step Undo can take */

  function ledgerHtml(items) {
    var total = 0, saved = 0;
    items.forEach(function (it) {
      total += it.price;
      if (it.compare && it.compare > it.price) saved += it.compare - it.price;
    });
    var cells = '<div class="cnc-wl-stat"><dt>Saved</dt><dd>' + items.length + "</dd></div>"
      + '<div class="cnc-wl-stat"><dt>Worth</dt><dd>' + money(total / 100) + "</dd></div>";
    if (saved > 0) {
      cells += '<div class="cnc-wl-stat cnc-wl-stat--save"><dt>You save</dt><dd>'
        + money(saved / 100) + "</dd></div>";
    }
    return cells;
  }

  function paintWishlist(root) {
    var items = savedItems();
    var grid = root.querySelector("[data-wl-grid]");
    var blank = root.querySelector("[data-wl-blank]");
    var ledger = root.querySelector("[data-wl-ledger]");
    var sub = root.querySelector("[data-wl-sub]");
    if (!grid) return;

    grid.classList.remove("cnc-wl-skel");
    grid.innerHTML = items.map(savedCardHtml).join("");
    grid.hidden = !items.length;
    if (blank) blank.hidden = !!items.length;
    if (ledger) {
      ledger.innerHTML = items.length ? ledgerHtml(items) : "";
      ledger.hidden = !items.length;
    }
    if (sub) {
      sub.textContent = !items.length
        ? "Tap the heart on any product to keep it here."
        : items.length === 1
          ? "1 item is waiting for you."
          : items.length + " items are waiting for you.";
    }

    /* Signature of what is on screen, so an external write (another tab, the
       cart drawer, the host theme's own heart) can be told apart from the
       echo of our own — and only the former forces a repaint. */
    grid.setAttribute("data-wl-sig", items.map(function (i) { return i.id; }).join(","));

    var rescan = api("rescan");
    if (rescan) rescan();
    hydrate(items, root);
  }

  /* What the saved meta cannot know: whether the thing is still in stock, and
     what its options are actually called. Both change after the heart was
     tapped, so both are read from the product itself — lazily, one small JSON
     each, and only for cards that made it onto the screen.

     Failure is silent by design. A wishlist that renders from cache and
     enriches when it can is strictly better than one that waits on a network
     round trip per card before showing anything. */
  var hydrated = {};

  function hydrate(items, root) {
    if (!("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        var id = en.target.getAttribute("data-wl-card");
        var it = items.filter(function (x) { return x.id === id; })[0];
        if (!it || !it.handle) return;
        if (hydrated[it.handle]) { applyLive(root, id, hydrated[it.handle]); return; }
        fetch("/products/" + it.handle + ".js", { headers: { Accept: "application/json" } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            if (!p) return;
            hydrated[it.handle] = p;
            applyLive(root, id, p);
          })
          .catch(function () { /* cached copy stands */ });
      });
    }, { rootMargin: "200px" });
    root.querySelectorAll("[data-wl-card]").forEach(function (c) { io.observe(c); });
  }

  function cardById(root, id) {
    var hit = null;
    root.querySelectorAll("[data-wl-card]").forEach(function (c) {
      if (c.getAttribute("data-wl-card") === String(id)) hit = c;
    });
    return hit;
  }

  function applyLive(root, id, p) {
    // Matched by attribute value rather than a built selector: ids are opaque
    // strings from someone else's store, and CSS.escape is not everywhere.
    var card = cardById(root, id);
    if (!card) return;
    var variants = p.variants || [];
    var inStock = variants.filter(function (v) { return v.available; });
    var move = card.querySelector(".cnc-wl-move");
    var hint = card.querySelector("[data-wl-hint]");

    if (move && !inStock.length) {
      move.classList.add("is-out");
      move.setAttribute("aria-disabled", "true");
      var lbl = move.querySelector("span");
      if (lbl) lbl.textContent = "Sold out";
    }

    /* Name the choice rather than counting it. "3 sizes" tells someone what
       the tap will ask them; "3 options" only tells them it will ask. */
    if (hint) {
      var opt = (p.options || [])[0];
      var name = (typeof opt === "string" ? opt : (opt && opt.name)) || "";
      var count = inStock.length || variants.length;
      if (variants.length > 1 && name && !/^title$/i.test(name)) {
        hint.hidden = false;
        hint.textContent = count + " " + (count === 1 ? name.toLowerCase()
          : /s$/i.test(name) ? name.toLowerCase() : name.toLowerCase() + "s");
      } else if (variants.length <= 1) {
        hint.hidden = true;
      }
    }

    /* A price that moved since it was hearted is the single most useful thing
       this page can tell someone, so it is corrected in place rather than
       left showing the number that was true last month. */
    var live = inStock[0] || variants[0];
    if (!live) return;
    var row = card.querySelector(".cnc-price-row");
    if (row) {
      row.outerHTML = priceRow({ price: live.price, compare: live.compare_at_price || 0 });
    }

    /* And the variant count, which decides whether the tap adds or asks. The
       saved sidecar recorded it at the moment of hearting; a merchant adding
       a size since then would otherwise have this button silently add the
       old one. The live product is the authority. */
    if (move) {
      var many = variants.length > 1;
      move.setAttribute("data-cnc-jsmulti", many ? "1" : "0");
      move.setAttribute("data-cnc-jsadd", many ? "" : String(live.id));
    }
  }

  function removeSaved(id, title) {
    var list = readWish();
    var at = list.indexOf(String(id));
    if (at === -1) return;
    var meta = readJson(WISH_META_KEY, {});
    lastRemoved = { id: String(id), at: at, meta: meta[id] };

    list.splice(at, 1);
    delete meta[id];
    writeJson(WISH_META_KEY, meta);
    writeWish(list);

    toast((title ? '"' + title + '"' : "Item") + " removed", "Undo", undoRemove);
  }

  function undoRemove() {
    if (!lastRemoved) return;
    var list = readWish();
    if (list.indexOf(lastRemoved.id) === -1) {
      // Back where it was, not appended. The order of a wishlist is the order
      // someone built it in, and Undo that reorders is not undo.
      list.splice(Math.min(lastRemoved.at, list.length), 0, lastRemoved.id);
    }
    if (lastRemoved.meta) {
      var meta = readJson(WISH_META_KEY, {});
      meta[lastRemoved.id] = lastRemoved.meta;
      writeJson(WISH_META_KEY, meta);
    }
    writeWish(list);
    lastRemoved = null;
  }

  function initWishlistPage(root) {
    if (root.dataset.wlReady === "1") return;
    root.dataset.wlReady = "1";
    paintWishlist(root);

    root.addEventListener("click", function (e) {
      var del = e.target.closest("[data-wl-remove]");
      if (!del) return;
      e.preventDefault();
      var card = del.closest("[data-wl-card]");
      var title = card ? (card.querySelector(".cnc-card-title") || {}).textContent : "";
      /* Collapse first, write after. Removing the node in the same frame as
         the store write makes the grid snap shut, and a list that reflows
         instantly is a list nobody trusts they can undo. */
      if (card && !reduceMotion) {
        card.classList.add("is-going");
        setTimeout(function () { removeSaved(del.getAttribute("data-wl-remove"), title); }, 200);
      } else {
        removeSaved(del.getAttribute("data-wl-remove"), title);
      }
    });

    /* Repaint only when the store genuinely differs from what is on screen.
       Our own writes announce themselves too, and repainting on those would
       throw away the collapse animation mid-flight. Comparing the signature
       means it does not matter who wrote, or whether they told us. */
    function resync() {
      var grid = root.querySelector("[data-wl-grid]");
      var now = savedItems().map(function (i) { return i.id; }).join(",");
      if (grid && grid.getAttribute("data-wl-sig") === now) return;
      paintWishlist(root);
    }
    document.addEventListener("cnc:wishlist-updated", function () { setTimeout(resync, 0); });
    window.addEventListener("storage", function (e) {
      if (e.key === WISH_KEY || e.key === WISH_META_KEY) resync();
    });
    document.addEventListener("cnc:added", function (e) {
      var d = (e && e.detail) || {};
      if (d.mode === "move") {
        toast("Moved to your bag", "View bag", function () {
          var open = api("openCart");
          if (open && api("hasCart") && window.CNC.hasCart()) open();
          else window.location.href = "/cart";
        });
      }
      setTimeout(resync, 0);
    });
  }

  /* ── Recently viewed rail ─────────────────────────────────────────────── */

  function initViewedRail(root) {
    if (root.dataset.rvReady === "1") return;
    root.dataset.rvReady = "1";

    var track = root.querySelector("[data-rv-track]");
    var fallback = root.querySelector("[data-rv-fallback]");
    var limit = parseInt(root.getAttribute("data-rv-limit"), 10) || 12;

    /* The product being looked at is not a product the customer needs
       recommending back to them. On a PDP the payload cnc-app.js records from
       is right there in the page, so read the id off it rather than guessing
       from the URL. */
    var self = "";
    var payload = document.querySelector("[data-cnc-viewed]");
    if (payload) {
      try { self = String((JSON.parse(payload.textContent) || {}).id || ""); }
      catch (e) { self = ""; }
    }

    /* The heading is the fallback's until there is history to justify the
       other one. Calling a row of bestsellers "Recently viewed" for someone
       who has viewed nothing is a lie a default tells, and the customer is
       the only one who can tell it is wrong.

       Both titles are read from ATTRIBUTES, never from the text on screen: the
       anti-flash script in the snippet may already have swapped that text
       before this file runs, and capturing it then would record the live
       title as the fallback one — after which the rail could never say
       anything else. */
    function swapCopy(live) {
      ["[data-rv-title]", "[data-rv-sub]"].forEach(function (sel) {
        var el = root.querySelector(sel);
        if (!el) return;
        var t = el.getAttribute(live ? "data-rv-live" : "data-rv-fb");
        if (t) el.textContent = t;
      });
    }

    function paint() {
      var items = viewedItems()
        .filter(function (it) { return it.id !== self; })
        .slice(0, limit);

      /* Nothing viewed yet is the normal state for a first visit, so the
         section shows the fallback row the server rendered — the merchant's
         collection, or the whole catalogue if they picked none. It is drawn
         with the real card snippet, so it is a full-quality row rather than
         an apology, and it is never nothing: a section that hides itself when
         unconfigured reads as broken rather than as unset. */
      if (!items.length) {
        if (track) { track.innerHTML = ""; track.hidden = true; }
        swapCopy(false);
        if (fallback) { fallback.hidden = false; root.hidden = false; }
        else root.hidden = true;
        paintNav(root);
        return;
      }
      root.hidden = false;
      swapCopy(true);
      if (fallback) fallback.hidden = true;
      if (track) {
        track.hidden = false;
        track.innerHTML = items.map(viewedCardHtml).join("");
      }
      var rescan = api("rescan");
      if (rescan) rescan();
      paintNav(root);
    }

    paint();
    document.addEventListener("cnc:wishlist-updated", function () {
      var rescan = api("rescan");
      if (rescan) rescan();
    });

    /* History changing under the rail is not hypothetical: cnc-app.js records
       a view from any product page, and a customer with two tabs open is
       browsing in one while this page sits in the other. Without this the rail
       keeps showing the fallback — under the fallback heading — for a customer
       who now has history, until they reload. */
    window.addEventListener("storage", function (e) {
      if (e.key === VIEWED_KEY) paint();
    });

    root.addEventListener("click", function (e) {
      var nav = e.target.closest("[data-rv-nav]");
      var track = rvActiveTrack(root);   // the live row, or the fallback row
      if (!nav || !track) return;
      e.preventDefault();
      // Just under a full page, so the card you were reading stays partly in
      // view and the movement is legible rather than a teleport.
      var step = Math.max(160, track.clientWidth * 0.8);
      track.scrollBy({
        left: Number(nav.getAttribute("data-rv-nav")) * step,
        behavior: reduceMotion ? "auto" : "smooth"
      });
    });

    [track, fallback].forEach(function (t) {
      if (t) t.addEventListener("scroll", function () { paintNav(root); }, { passive: true });
    });
    window.addEventListener("resize", function () { paintNav(root); });
    window.addEventListener("load", function () { paintNav(root); });
  }

  /* Whichever row is actually on screen. First-time visitors see the
     server-rendered fallback row, and the arrows used to measure (and
     scroll) the hidden, empty live row instead — so they never appeared. */
  function rvActiveTrack(root) {
    var live = root.querySelector("[data-rv-track]");
    if (live && !live.hidden) return live;
    var fb = root.querySelector("[data-rv-fallback]");
    if (fb && !fb.hidden) return fb;
    return live || fb;
  }

  /* Arrows reflect reality: gone when there is nothing to scroll, disabled at
     each end. Anything else is a control that lies about what it will do. */
  function paintNav(root) {
    var track = rvActiveTrack(root);
    var prev = root.querySelector('[data-rv-nav="-1"]');
    var next = root.querySelector('[data-rv-nav="1"]');
    if (!track || !prev || !next) return;
    var over = track.scrollWidth - track.clientWidth > 4;
    prev.hidden = next.hidden = !over;
    if (!over) return;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
  }

  /* ── Cart actions from a client-rendered card ─────────────────────────────
     Delegated from document once, so cards painted at any later moment need
     no re-binding. Two routes out:

       one variant   straight through window.CNC.addToCart
       many          the Pick & Pack sheet, because choosing a size on the
                     customer's behalf is a guess, and a wrong variant quietly
                     added to a bag is worse than one extra tap.            */
  function initJsCardAdd() {
    if (document.documentElement.dataset.cncJsAdd === "1") return;
    document.documentElement.dataset.cncJsAdd = "1";

    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-cnc-jsadd]");
      if (!btn || btn.classList.contains("is-out")) return;
      e.preventDefault();

      var d = btn.dataset;
      var mode = d.cncJsmode === "move" ? "move" : "add";
      var multi = d.cncJsmulti === "1" || !d.cncJsadd;

      if (multi) {
        var open = api("openPicker");
        if (open) {
          open({ handle: d.cncJshandle, id: d.cncJsid, url: d.cncJsurl, mode: mode, opener: btn });
        } else if (d.cncJsurl) {
          window.location.href = d.cncJsurl;
        }
        return;
      }

      var add = api("addToCart");
      if (!add) { if (d.cncJsurl) window.location.href = d.cncJsurl; return; }

      var label = btn.querySelector("span");
      var was = label ? label.textContent : "";
      btn.classList.add("is-busy");
      if (label) label.textContent = mode === "move" ? "Moving…" : "Adding…";

      /* Out of the wishlist BEFORE the add, not after. The theme re-renders
         on a successful add, and anything still hearted at that moment comes
         straight back as a card for something now sitting in the bag. */
      if (mode === "move") dropFromWishlist(d.cncJsid);

      add(d.cncJsadd, 1)
        .then(function () {
          btn.classList.remove("is-busy");
          if (label) label.textContent = mode === "move" ? "Moved" : "Added";
          document.dispatchEvent(new CustomEvent("cnc:added", {
            detail: { variant: d.cncJsadd, product: d.cncJsid, qty: 1, mode: mode }
          }));
          if (mode === "add") {
            toast("Added to your bag", "View bag", function () {
              var openCart = api("openCart");
              if (openCart && api("hasCart") && window.CNC.hasCart()) openCart();
              else window.location.href = "/cart";
            });
            setTimeout(function () { if (label) label.textContent = was; }, 1600);
          }
        })
        .catch(function () {
          btn.classList.remove("is-busy");
          if (label) label.textContent = "Try again";
        });
    });
  }

  function dropFromWishlist(id) {
    var list = readWish();
    var at = list.indexOf(String(id));
    if (at === -1) return;
    list.splice(at, 1);
    var meta = readJson(WISH_META_KEY, {});
    delete meta[id];
    writeJson(WISH_META_KEY, meta);
    writeWish(list);
  }

  /* ── Boot ─────────────────────────────────────────────────────────────── */

  function boot() {
    try {
      initJsCardAdd();
      document.querySelectorAll("[data-cnc-wishlist]").forEach(initWishlistPage);
      document.querySelectorAll("[data-cnc-rv]").forEach(initViewedRail);
    } catch (err) {
      console.error("[cnc-wishlist] boot failed", err);
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", boot);
})();
